import { ChevronsDown, ChevronsUp, SlidersHorizontal } from 'lucide-react';

import { cn } from '@/lib/shadcn/utils';
import type { EmphasisLevel } from '@/modules/jobs/signal/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/modules/ui/components/dropdown-menu/DropdownMenu';

type Level = EmphasisLevel | 'Normal';

const LEVELS: { value: Level; label: string }[] = [
  { value: 'High', label: 'Lean on it more' },
  { value: 'Normal', label: 'Normal' },
  { value: 'Low', label: 'Lean on it less' },
];

const isLevel = (value: string): value is Level =>
  LEVELS.some((level) => level.value === value);

interface SkillEmphasisMenuProps {
  /** The skill as the chip shows it. */
  skill: string;
  level: Level;
  onChange: (level: Level) => void;
}

/** How much the search leans on one skill: a chip's arrow, up for more and down for less. */
export function SkillEmphasisMenu({
  skill,
  level,
  onChange,
}: SkillEmphasisMenuProps) {
  const Icon =
    level === 'High'
      ? ChevronsUp
      : level === 'Low'
        ? ChevronsDown
        : SlidersHorizontal;
  const current = LEVELS.find((option) => option.value === level)!.label;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`How much the search leans on ${skill}: ${current}`}
          title={current}
          className={cn(
            'rounded-full p-0.5',
            level === 'Normal'
              ? 'hover:text-foreground text-neutral-500'
              : 'text-foreground',
          )}
        >
          <Icon className="size-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>
          How much the search leans on {skill}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={level}
          onValueChange={(value) => isLevel(value) && onChange(value)}
        >
          {LEVELS.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
